/**
 * Offline Message Queue - Background Sync Integration
 * 
 * Maneja el envío de mensajes cuando el usuario está offline.
 * Los mensajes se guardan en IndexedDB y se envían automáticamente
 * cuando vuelve la conexión (via Background Sync API).
 * 
 * NOTA: Background Sync no funciona en iOS Safari, pero sí en
 * Android Chrome y Desktop browsers.
 */

// Estado local
let swRegistration = null;
let messageListeners = [];

/**
 * Inicializar el módulo con el SW registration
 */
export function initOfflineQueue(registration) {
  swRegistration = registration;
  
  // Escuchar mensajes del SW
  navigator.serviceWorker.addEventListener("message", handleSWMessage);
  
  console.log("[OfflineQueue] Initialized with Background Sync:", "sync" in registration);
}

/**
 * Verificar si Background Sync está disponible
 */
export function isBackgroundSyncAvailable() {
  return swRegistration !== null && "sync" in swRegistration;
}

/**
 * Verificar si estamos offline
 */
export function isOffline() {
  return !navigator.onLine;
}

/**
 * Agregar mensaje a la cola offline
 */
export async function queueMessage(contactId, message, contactName, authToken) {
  if (!swRegistration?.active) {
    return { success: false, error: "Service Worker no activo" };
  }
  
  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();
    
    messageChannel.port1.onmessage = (event) => {
      resolve(event.data);
      notifyListeners();
    };
    
    swRegistration.active.postMessage({
      type: "QUEUE_MESSAGE",
      payload: {
        contactId,
        contactName,
        message,
        authToken
      }
    }, [messageChannel.port2]);
  });
}

/**
 * Obtener mensajes pendientes
 */
export async function getPendingMessages() {
  if (!swRegistration?.active) {
    return [];
  }
  
  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();
    
    messageChannel.port1.onmessage = (event) => {
      if (event.data.success) {
        resolve(event.data.messages || []);
      } else {
        console.error("[OfflineQueue] Error getting messages:", event.data.error);
        resolve([]);
      }
    };
    
    swRegistration.active.postMessage({
      type: "GET_PENDING_MESSAGES"
    }, [messageChannel.port2]);
  });
}

/**
 * Reintentar envío de mensajes pendientes
 */
export async function retryPendingMessages() {
  if (!swRegistration?.active) {
    return false;
  }
  
  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();
    
    messageChannel.port1.onmessage = (event) => {
      resolve(event.data.success);
      notifyListeners();
    };
    
    swRegistration.active.postMessage({
      type: "RETRY_SEND"
    }, [messageChannel.port2]);
  });
}

/**
 * Eliminar mensaje de la cola
 */
export async function deletePendingMessage(id) {
  if (!swRegistration?.active) {
    return false;
  }
  
  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();
    
    messageChannel.port1.onmessage = (event) => {
      resolve(event.data.success);
      notifyListeners();
    };
    
    swRegistration.active.postMessage({
      type: "DELETE_PENDING_MESSAGE",
      id
    }, [messageChannel.port2]);
  });
}

/**
 * Suscribirse a cambios en la cola
 */
export function subscribeToPendingMessages(callback) {
  messageListeners.push(callback);
  
  // Enviar estado actual inmediatamente
  getPendingMessages().then(callback);
  
  // Devolver función para cancelar suscripción
  return () => {
    messageListeners = messageListeners.filter(cb => cb !== callback);
  };
}

/**
 * Notificar a todos los listeners
 */
async function notifyListeners() {
  const messages = await getPendingMessages();
  messageListeners.forEach(cb => cb(messages));
}

/**
 * Manejar mensajes del Service Worker
 */
function handleSWMessage(event) {
  const { data } = event;
  
  if (data.type === "MESSAGE_SENT" || data.type === "MESSAGE_FAILED") {
    console.log("[OfflineQueue] SW message:", data.type, data);
    notifyListeners();
    
    // Emitir evento custom para que la UI pueda reaccionar
    window.dispatchEvent(new CustomEvent("offlineMessageUpdate", { detail: data }));
  }
}

/**
 * Hook para detectar cambios de conexión
 */
export function setupConnectionListeners() {
  const handleOnline = () => {
    console.log("[OfflineQueue] Connection restored");
    
    // Intentar enviar mensajes pendientes
    if (isBackgroundSyncAvailable()) {
      console.log("[OfflineQueue] Background Sync will handle pending messages");
    } else {
      console.log("[OfflineQueue] Manual retry (no Background Sync)");
      retryPendingMessages();
    }
    
    window.dispatchEvent(new CustomEvent("connectionStatusChange", { 
      detail: { online: true } 
    }));
  };
  
  const handleOffline = () => {
    console.log("[OfflineQueue] Connection lost");
    window.dispatchEvent(new CustomEvent("connectionStatusChange", { 
      detail: { online: false } 
    }));
  };
  
  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
  
  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  };
}

export default {
  initOfflineQueue,
  isBackgroundSyncAvailable,
  isOffline,
  queueMessage,
  getPendingMessages,
  retryPendingMessages,
  deletePendingMessage,
  subscribeToPendingMessages,
  setupConnectionListeners
};
